import {
  useState, useMemo 
} from 'react';
import {
  useLocation, useNavigate 
} from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  useRecipes, CulinaryHelpPanel 
} from '@/features/recipes';
import type {
  Recipe, RecipeFormData 
} from '@/features/recipes';
import { toast } from 'sonner';
import { TABS } from './recipe-constants';
import { CostingView } from './costing-view';
import { LibraryView } from './library-view';

export function RecipesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDietary, setSelectedDietary] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState<Recipe | null>(null);
  const [viewingRecipe, setViewingRecipe] = useState<Recipe | null>(null);

  const activeTab = useMemo(() => {
    if (location.pathname.includes('/menu-items')) return 'menu-items';
    if (location.pathname.includes('/sub-recipes')) return 'sub-recipes';
    if (location.pathname.includes('/components')) return 'components';
    if (location.pathname.includes('/costing')) return 'costing';
    return 'menu-items';
  }, [location.pathname]);

  const effectiveType = useMemo(() => {
    if (activeTab === 'menu-items') return 'finished' as const;
    if (activeTab === 'sub-recipes') return 'sub-recipe' as const;
    if (activeTab === 'components') return 'component' as const;
    return undefined;
  }, [activeTab]);

  const filters = useMemo(() => ({
    type: effectiveType,
    search: searchQuery || undefined,
    dietaryFlag: selectedDietary || undefined,
  }), [effectiveType, searchQuery, selectedDietary]);

  const {
    data, isLoading, error, refetch, isFetching 
  } = useRecipes({ filters });
  const recipes = data?.data ?? [];

  const { data: allData } = useRecipes({});
  const typeCounts = useMemo(() => {
    const counts = {
      all: 0,
      finished: 0,
      'sub-recipe': 0,
      component: 0 
    };
    for (const recipe of allData?.data ?? []) {
      counts.all++;
      counts[recipe.type]++;
    }
    return counts;
  }, [allData]);

  const handleSaveRecipe = (formData: RecipeFormData) => {
    if (editingRecipe) {
      toast.success(`Updated ${formData.name}`);
    } else {
      toast.success(`Created ${formData.name}`);
    }
    setShowAddModal(false);
    setEditingRecipe(null);
    refetch();
  };

  const handleBulkUpload = (file: File) => {
    toast.success(`Processing ${file.name}...`);
    setTimeout(() => {
      toast.success('Imported 15 recipes');
      refetch();
    }, 1500);
  };

  const pageTitle = useMemo(() => {
    switch (activeTab) {
      case 'menu-items': return 'Menu Items';
      case 'sub-recipes': return 'Sub-Recipes';
      case 'components': return 'Components';
      case 'costing': return 'Recipe Costing';
      default: return 'Culinary';
    }
  }, [activeTab]);

  const pageDescription = useMemo(() => {
    switch (activeTab) {
      case 'menu-items': return 'Finished dishes that appear on menus';
      case 'sub-recipes': return 'Reusable preparations like sauces, stocks, and marinades';
      case 'components': return 'Base building blocks like spice blends and compound butters';
      case 'costing': return 'Analyze food costs and pricing across all recipes';
      default: return 'Manage your culinary library';
    }
  }, [activeTab]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{pageTitle}</h1>
          <p className="text-muted-foreground">{pageDescription}</p>
        </div>
        <div className="flex items-center gap-2">
          <CulinaryHelpPanel />
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="border-b">
        <nav className="flex gap-4">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => navigate(tab.href)}
                className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:border-muted-foreground/50 hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === 'costing' && <CostingView recipes={recipes} totalCount={typeCounts.all} />}

      {activeTab !== 'costing' && (
        <LibraryView
          recipes={recipes}
          allRecipes={allData?.data ?? []}
          typeCounts={typeCounts}
          isLoading={isLoading}
          error={error}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedDietary={selectedDietary}
          setSelectedDietary={setSelectedDietary}
          showAddModal={showAddModal}
          setShowAddModal={setShowAddModal}
          editingRecipe={editingRecipe}
          setEditingRecipe={setEditingRecipe}
          viewingRecipe={viewingRecipe}
          setViewingRecipe={setViewingRecipe}
          onSaveRecipe={handleSaveRecipe}
          onBulkUpload={handleBulkUpload}
          onRefetch={refetch}
          activeTab={activeTab}
        />
      )}
    </div>
  );
}
